/**
 * SubscriptionViewController — EPIC-055 / 55.11.
 *
 * Pilote la page `/abonnement.html` (nouveau modèle « mois actif »).
 * Un seul round-trip d'hydratation via `GET /billing/subscription-overview`
 * qui agrège : subscription, mandate, charges (12 derniers), nextCharge,
 * creationFee, issuance.
 *
 * Sections rendues :
 *   1. Mon abonnement (mode tarifaire + statut)
 *   2. Crédit de mois inclus (Parcours A — visible si paiement frais création tracé)
 *   3. Mandat SEPA B2B (état dynamique : not configured / pending / active)
 *   4. Historique des 12 derniers mois (`subscription_charges`)
 *   5. Prochaine pré-notification (charge `pending` / `pre_notified`)
 *
 * Actions :
 *   - openMandateSignature → redirige vers `signatureUrl` Qonto. Si aucun
 *     `signatureUrl` connu (jamais initié), appelle `POST /billing/mandate`
 *     pour en obtenir un, puis redirige.
 *   - revokeMandate → `toast.confirm(...)` avant `DELETE /billing/mandate`.
 *     Si bloqué côté serveur (charges ouvertes, 422), toast d'erreur.
 *
 * Bannière de suspension d'émission : déléguée au controller
 * `issuance-gate` monté séparément (réutilisable wizard de facturation).
 *
 * Vocabulaire UI (CLAUDE.md) : aucune mention de Qonto / Sweego / NotaFacto /
 * PDP / OCR dans les libellés visibles. Les CTA emploient « prélèvement
 * automatique » et « mandat de paiement ».
 *
 * Convention CLAUDE.md : pas d'HTML inline en JS ; cloneTemplate +
 * fillSlots + classList/textContent uniquement.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import toast from "/js/services/toast.js";

const BILLING_MODE_LABEL = {
  active_month: "Mois actif — 12 € HT / mois actif",
  annual: "Forfait annuel",
};

const SUBSCRIPTION_STATUS_LABEL = {
  active: "Actif",
  past_due: "En attente de régularisation",
  cancelled: "Résilié",
  expired: "Expiré",
};

const SUBSCRIPTION_STATUS_PILL_CLASS = {
  active: "nm-pill nm-pill--olive",
  past_due: "nm-pill nm-pill--ochre",
  cancelled: "nm-pill",
  expired: "nm-pill",
};

const MANDATE_PENDING_LABEL = {
  pending: "En attente de signature",
  pending_bank_validation: "En attente de validation bancaire",
};

const CHARGE_STATUS_LABEL = {
  pending: "En attente",
  pre_notified: "Pré-notifié",
  debited: "Prélevé",
  failed: "Impayé",
  waived: "Inclus (crédit)",
};

const CHARGE_STATUS_PILL_CLASS = {
  pending: "nm-pill nm-pill--ochre",
  pre_notified: "nm-pill nm-pill--ochre",
  debited: "nm-pill nm-pill--olive",
  failed: "nm-pill",
  waived: "nm-pill nm-pill--olive",
};

const MONTH_NAMES = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export default class SubscriptionViewController extends BaseController {
  static targets = [
    // Section 1
    "billingModeLabel",
    "statusPill",
    "statusLabel",
    // Section 2
    "creditBlock",
    "creditRemainingLabel",
    "creationFeeAmount",
    "creationFeePaidAt",
    "creditMessage",
    // Section 3
    "mandateNotConfigured",
    "mandatePending",
    "mandatePendingLabel",
    "mandateActive",
    "mandateIban",
    "mandateActivatedAt",
    "configureMandateBtn",
    "resumeMandateBtn",
    "revokeMandateBtn",
    // Section 4
    "chargesList",
    // Section 5
    "nextChargeMessage",
  ];

  async connect() {
    this.mandateSignatureUrl = null;
    await this.refresh();
  }

  async refresh() {
    let data;
    try {
      data = await api.getSubscriptionOverview();
    } catch (_error) {
      // 401 géré globalement par api.js. Autres erreurs : pas d'écran vide
      // dédié — on laisse les libellés « — » lisibles et un toast informe.
      toast.error("Impossible de charger votre abonnement. Réessayez dans un instant.");
      return;
    }

    this.renderSubscription(data.subscription);
    this.renderCreditSection(data.creationFee);
    this.renderMandateSection(data.mandate);
    this.renderCharges(data.charges || []);
    this.renderNextCharge(data.nextCharge);
  }

  renderSubscription(subscription) {
    if (subscription === null) {
      this.billingModeLabelTarget.textContent = "Aucun abonnement actif";
      this.statusLabelTarget.textContent = "—";
      this.statusPillTarget.className = "nm-pill";
      return;
    }

    this.billingModeLabelTarget.textContent =
      BILLING_MODE_LABEL[subscription.billingMode] || subscription.billingMode;

    const statusLabel = SUBSCRIPTION_STATUS_LABEL[subscription.status] || subscription.status;
    this.statusLabelTarget.textContent = statusLabel;
    const pillClass = SUBSCRIPTION_STATUS_PILL_CLASS[subscription.status] || "nm-pill";
    // Conserver les enfants (.nm-pill__dot + span data-target=statusLabel) ; on
    // réécrit uniquement les classes du conteneur.
    this.statusPillTarget.className = pillClass;
  }

  renderCreditSection(creationFee) {
    if (creationFee === null || creationFee.paidAt === null) {
      this.creditBlockTarget.hidden = true;
      return;
    }

    this.creditBlockTarget.hidden = false;

    const remaining = Number(creationFee.creditRemaining ?? 0);
    if (remaining <= 0) {
      this.creditRemainingLabelTarget.textContent = "Épuisé";
      this.creditMessageTarget.textContent =
        "Votre crédit est épuisé. Les prochains mois actifs seront prélevés via votre mandat de paiement.";
    } else {
      this.creditRemainingLabelTarget.textContent = `${remaining} mois actif${remaining > 1 ? "s" : ""} inclus`;
      this.creditMessageTarget.textContent =
        "Ce crédit est décompté uniquement les mois où vous facturez.";
    }

    this.creationFeeAmountTarget.textContent = this.formatAmountHt(creationFee.amountHt);
    this.creationFeePaidAtTarget.textContent = this.formatShortDate(creationFee.paidAt);
  }

  renderMandateSection(mandate) {
    this.mandateNotConfiguredTarget.hidden = true;
    this.mandatePendingTarget.hidden = true;
    this.mandateActiveTarget.hidden = true;
    this.mandateSignatureUrl = null;

    if (mandate === null) {
      this.mandateNotConfiguredTarget.hidden = false;
      return;
    }

    this.mandateSignatureUrl = mandate.signatureUrl || null;

    if (mandate.status === "active") {
      this.mandateActiveTarget.hidden = false;
      this.mandateIbanTarget.textContent = mandate.ibanMasked || "—";
      this.mandateActivatedAtTarget.textContent =
        this.formatShortDate(mandate.activatedAt) || "—";
      return;
    }

    if (mandate.status === "pending" || mandate.status === "pending_bank_validation") {
      this.mandatePendingTarget.hidden = false;
      this.mandatePendingLabelTarget.textContent =
        MANDATE_PENDING_LABEL[mandate.status] || "En attente";
      return;
    }

    // revoked / expired → repropose la configuration
    this.mandateNotConfiguredTarget.hidden = false;
  }

  renderCharges(charges) {
    this.chargesListTarget.replaceChildren();

    if (charges.length === 0) {
      const emptyFragment = this.cloneTemplate("tpl-charges-empty");
      if (emptyFragment !== null) {
        this.chargesListTarget.appendChild(emptyFragment);
      }
      return;
    }

    for (const charge of charges) {
      const fragment = this.cloneTemplate("tpl-charge-row");
      if (fragment === null) {
        continue;
      }
      const statusLabel = CHARGE_STATUS_LABEL[charge.status] || charge.status;
      const debitedAt = charge.debitedAt ? this.formatShortDate(charge.debitedAt) : "—";
      this.fillSlots(fragment, {
        yearMonth: this.formatYearMonth(charge.yearMonth),
        amount: this.formatAmountHt(charge.amountHt),
        statusLabel,
        debitedAt,
      });

      const pill = fragment.querySelector(".nm-pill");
      if (pill !== null) {
        pill.className = CHARGE_STATUS_PILL_CLASS[charge.status] || "nm-pill";
      }

      this.chargesListTarget.appendChild(fragment);
    }
  }

  renderNextCharge(nextCharge) {
    if (nextCharge === null) {
      this.nextChargeMessageTarget.textContent =
        "Aucun prélèvement prévu ce mois-ci — mois sans activité.";
      return;
    }

    const amount = this.formatAmountHt(nextCharge.amountHt);
    const monthLabel = this.formatYearMonth(nextCharge.yearMonth);

    if (nextCharge.preNotificationDate !== null) {
      const debitDate = this.formatShortDate(nextCharge.preNotificationDate);
      this.nextChargeMessageTarget.textContent =
        `Votre prochain prélèvement de ${amount} (mois ${monthLabel}) est pré-notifié pour le ${debitDate}.`;
      return;
    }

    this.nextChargeMessageTarget.textContent =
      `Un prélèvement de ${amount} est prévu pour le mois ${monthLabel}. Vous recevrez une pré-notification quelques jours avant le débit.`;
  }

  async openMandateSignature(event) {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    if (this.mandateSignatureUrl !== null && this.mandateSignatureUrl !== "") {
      window.location.assign(this.mandateSignatureUrl);
      return;
    }

    try {
      const result = await api.post("/billing/mandate", {});
      if (result && result.signatureUrl) {
        window.location.assign(result.signatureUrl);
        return;
      }
      toast.error("Le service de paiement est indisponible. Réessayez dans quelques minutes.");
    } catch (error) {
      const message = error instanceof APIError && error.message
        ? error.message
        : "Le service de paiement est indisponible. Réessayez dans quelques minutes.";
      toast.error(message);
    }
  }

  async revokeMandate() {
    const confirmed = await toast.confirm(
      "Cette action désactive votre mandat de prélèvement automatique. Sans mandat actif, l'émission de nouvelles factures sera temporairement suspendue.",
      { confirmLabel: "Révoquer le mandat", cancelLabel: "Annuler" },
    );
    if (!confirmed) {
      return;
    }

    try {
      await api.revokeSubscriptionMandate();
      toast.success("Mandat révoqué. Configurez un nouveau prélèvement pour reprendre l'émission de factures.");
      await this.refresh();
    } catch (error) {
      if (error instanceof APIError && error.data?.error?.code === "mandate_revocation_blocked") {
        toast.error("Impossible de révoquer le mandat tant que des prélèvements sont en cours. Régularisez d'abord ces mois.");
        return;
      }
      toast.error("La révocation a échoué. Réessayez dans un instant.");
    }
  }

  formatAmountHt(amountHt) {
    if (amountHt === null || amountHt === undefined || amountHt === "") {
      return "—";
    }
    const value = Number(amountHt);
    if (Number.isNaN(value)) {
      return String(amountHt);
    }
    return value.toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " HT";
  }

  formatYearMonth(yearMonth) {
    if (typeof yearMonth !== "string" || yearMonth.length < 7) {
      return "—";
    }
    const [yearStr, monthStr] = yearMonth.split("-");
    const monthIdx = Number(monthStr) - 1;
    if (monthIdx < 0 || monthIdx > 11) {
      return yearMonth;
    }
    return `${MONTH_NAMES[monthIdx]} ${yearStr}`;
  }
}
